import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
    Box,
    Button,
    Drawer,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faPlay,
    faUndo,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { WorkItem, WorkItemUpdate, WorkItemSafety, workItemsApi, Attention, SafetyVerdict, SourceFamily } from '../api/workItems';
import { documentsApi } from '../api/documents';
import client from '../api/client';
import { getWorkItemCreatedFromLabel, getWorkItemDisplayTitle, getWorkItemSourceInfo, getWorkItemStateLabel, linkedEmailMessageId } from '../utils/workItemDisplay';
import { sourceKeyLabel } from '../utils/sourceKeyLabel';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { fetchSimilarItemSuggestions } from '../api/classificationSuggestions';
import { domainsApi, DomainConfig } from '../api/domains';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { calendarYmdInZone, dueBadgeFromYmd, effectiveUserTimeZone, extractYmdFromDue } from '../utils/userTime';
import ConfirmationDialog from './ConfirmationDialog';
import WorkItemDrawerHeader, { SourceDocumentState } from './WorkItemDrawerHeader';
import WorkItemLineageSection from './WorkItemLineageSection';
import WorkItemClassificationPanel from './WorkItemClassificationPanel';
import WorkItemTrustStrip, { deriveTrustStripViewModel } from './WorkItemTrustStrip';
import WorkItemDrawerActionsMenu from './WorkItemDrawerActionsMenu';
import WorkItemSourceTeachStrip from './WorkItemSourceTeachStrip';
import WorkItemMergeAttachStrip, { isMergeAttachEligible } from './WorkItemMergeAttachStrip';
import {
    AppliedSourceRule,
    currentSourceRuleAttention,
    isSourceTeachEligible,
} from './sourceTeachEligibility';
import DrawerTriageActionRow from './DrawerTriageActionRow';
import DrawerSourceHomeAccordion, { SourceHomePanel } from './DrawerSourceHomeAccordion';
import type { TriageActions } from './DrawerFacetTypes';
import DrawerFacetTriage, { buildOverviewModel } from './DrawerFacetTriage';
import { visuallyHiddenSx } from '../utils/visuallyHidden';
import DrawerFacetWork from './DrawerFacetWork';
import DrawerFacetIntel, { hasIntelContent } from './DrawerFacetIntel';
import {
    Facet,
    DecisionGuide,
    ScamRisk,
    InvoiceData,
    ContactInfo,
    ExtractionFields,
    NoticeData,
    DomainMenuItem,
    toDateInputValue,
} from './DrawerFacetTypes';
import { draftFacetSaveDiffersFromItem } from '../utils/workItemDraftDiff';
import { isBestGuessPlacement } from '../utils/classificationBestGuess';
import { deriveDrawerStakes, deriveNextStep } from '../utils/deriveNextStep';
import type { NextStepCtaKind } from '../utils/deriveNextStep';
import { buildGmailThreadDeepLink } from '../utils/gmailWebUrls';
import {
    drawerStatusLabel,
    dueStatusLine,
    formatDerivedTasks,
    lifeAreaChipLabel,
    quietProvenanceFact,
    readSenderFromNotes,
    sourceKindChipLabel,
    sourceSummaryText,
} from '../utils/drawerChromeCopy';
import { isSourceHomeItem, mayHaveSourceFamily } from '../utils/sourceFamily';
import { documentFamilyWorkbenchAnchor, workbenchPathForDocumentFamily } from '../utils/workbenchDocumentFamily';
import SourceFamilyStrip, { SourceFamilyProgress, SourceHomeSteps } from './SourceFamilyStrip';
import DrawerRiskAlert, { emailScamRisk } from './DrawerRiskAlert';
import { DrawerProvenanceLine, DrawerSourceTile } from './DrawerSourceChrome';
import type { DrawerNavigation } from '../hooks/useDrawerNavigation';

interface WorkItemDrawerProps {
    open: boolean;
    onClose: () => void;
    item: WorkItem | null;
    onUpdate: (updatedItem: WorkItem, sourceRule?: AppliedSourceRule) => void;
    onDelete: (deletedId: string) => void;
    initialDraft?: WorkItemUpdate;
    /** Area a new item starts in (Today's selected area). Without it, the profile's area. */
    newItemDomain?: string;
    /** Host-provided position in an ordered list; enables prev/next, auto-advance, and j/k. */
    navigation?: DrawerNavigation;
    /** Open another next step from this source in the same drawer. */
    onOpenFamilyMember?: (item: WorkItem) => void;
}

export type DrawerAutoAdvancePref = 'triage' | 'always' | 'off';
export const DRAWER_AUTO_ADVANCE_STORAGE_KEY = 'drawer.autoAdvance';

const readAutoAdvancePref = (): DrawerAutoAdvancePref => {
    try {
        const raw = window.localStorage?.getItem(DRAWER_AUTO_ADVANCE_STORAGE_KEY);
        return raw === 'always' || raw === 'off' ? raw : 'triage';
    } catch {
        return 'triage';
    }
};

const isEditableKeyTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return true;
    // MUI Menu / Select popovers own their own arrow-key handling.
    return Boolean(target.closest('[role="menu"], [role="menuitem"], [role="listbox"], [role="option"]'));
};

const parseDateForDrawerMath = (value: string): Date | null => {
    const localDatePrefix = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (localDatePrefix) {
        return new Date(
            Number(localDatePrefix[1]),
            Number(localDatePrefix[2]) - 1,
            Number(localDatePrefix[3]),
        );
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatLocalDrawerDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const addDays = (d: Date, days: number) => {
    const out = new Date(d);
    out.setDate(out.getDate() + days);
    return formatLocalDrawerDate(out) + 'T00:00:00';
};

// Default prep times (matches backend)
const DEFAULT_PREP_TIMES: Record<string, number> = {
    "interview": 3,
    "meeting": 1,
    "flight": 1,
    "deadline": 0,
    "presentation": 5,
    "exam": 7,
    "appointment": 1,
    "default": 2
};

const DEFAULT_DOMAIN_FIELDS: Record<string, Record<string, string>> = {
    job_hunt: {
        company: "Company",
        job_title: "Position",
        status: "Status",
        location: "Location",
        salary_range: "Salary",
    },
    parenthood: {
        school: "School",
        child: "Child",
        event_type: "Event type",
        event_date: "Event date",
        location: "Location",
        requires_action: "Action required",
    },
    finance: {
        entity: "Institution",
        status: "Status",
        due_date: "Due date",
    },
    business: {
        entity: "Client",
        status: "Status",
        due_date: "Due date",
    },
    personal: {
        entity: "Entity",
        status: "Status",
        due_date: "Due date",
    },
    health: {
        entity: "Provider",
        status: "Status",
        due_date: "Due date",
    },
};

// Job-named tabs. Test IDs keep the historical facet names so e2e/unit selectors stay stable.
const FACET_TABS: Array<{ facet: Facet; label: string; testId: string }> = [
    { facet: 'triage', label: 'Overview', testId: 'WB_DRAWER_TAB_TRIAGE' },
    { facet: 'work', label: 'Edit', testId: 'WB_DRAWER_TAB_WORK' },
    { facet: 'intel', label: 'Evidence', testId: 'WB_DRAWER_TAB_INTEL' },
];

const WB_DRAWER_DIRTY_TEST_IDS = {
    save: { testId: 'WB_DRAWER_DIRTY_SAVE' },
    discard: { testId: 'WB_DRAWER_DIRTY_DISCARD' },
    cancel: { testId: 'WB_DRAWER_DIRTY_CANCEL' },
} as const;

const WorkItemDrawer: React.FC<WorkItemDrawerProps> = ({ open, onClose, item, onUpdate, onDelete, initialDraft, newItemDomain, navigation, onOpenFamilyMember }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const motionTransition = prefersReducedMotion
        ? '0ms linear'
        : '220ms cubic-bezier(0.22, 1, 0.36, 1)';
    const compactHeaderScrollThreshold = isSmallScreen ? 4 : 6;
    const { bootstrap, loading: bootstrapLoading, error: bootstrapError, refresh: refreshBootstrap } = useAppBootstrap();
    const onMailboxGmailAuthIssue = useCallback(() => {
        void refreshBootstrap();
    }, [refreshBootstrap]);
    const [draft, setDraft] = useState<WorkItemUpdate>({});
    const newDraftBaselineRef = useRef<WorkItemUpdate>({});
    const dirtyRef = useRef(false);
    const [saving, setSaving] = useState(false);
    const [isCompactHeader, setIsCompactHeader] = useState(false);
    const [emailViewerExpanded, setEmailViewerExpanded] = useState(false);
    const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [showEventDate, setShowEventDate] = useState(false);
    const [suggestedActionDate, setSuggestedActionDate] = useState<string | null>(null);
    const [prepDaysUsed, setPrepDaysUsed] = useState<number | null>(null);
    const [detectedEventType, setDetectedEventType] = useState<string | null>(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [toastUndo, setToastUndo] = useState<(() => void | Promise<void>) | null>(null);
    const [similarItemSnackbarOpen, setSimilarItemSnackbarOpen] = useState(false);
    const [similarItemReadyBatchId, setSimilarItemReadyBatchId] = useState<string | null>(null);
    const [similarItemStatusMessage, setSimilarItemStatusMessage] = useState<string | null>(null);
    const similarItemPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [domainOptions, setDomainOptions] = useState<DomainConfig[]>([]);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [confirmRerunEnrichment, setConfirmRerunEnrichment] = useState(false);
    const [rawSourceDocumentState, setSourceDocumentState] = useState<SourceDocumentState>('none');
    const [checkedDocumentId, setCheckedDocumentId] = useState<string | null>(null);
    const sourceDocumentState: SourceDocumentState = checkedDocumentId === (item?.document_id || null)
        ? rawSourceDocumentState : item?.document_id ? 'checking' : 'none';
    const [sourceDocumentMessage, setSourceDocumentMessage] = useState<string>('');
    const [trustDetailsExpanded, setTrustDetailsExpanded] = useState(false);
    const [autoAdvancePref, setAutoAdvancePref] = useState<DrawerAutoAdvancePref>(readAutoAdvancePref);
    const [family, setFamily] = useState<SourceFamily | null>(null);
    const [familyOwnerId, setFamilyOwnerId] = useState('');
    const familyRef = useRef<SourceFamily | null>(null);
    familyRef.current = family;
    const openItemIdRef = useRef<string | null>(item?.id ?? null);
    openItemIdRef.current = item?.id ?? null;
    const [viewingSource, setViewingSource] = useState(false);
    const familyNavSeqRef = useRef(0);
    const familyNavWatchRef = useRef<{ id: string | null; open: boolean } | null>(null);
    const invalidateInFlightFamilyNav = useCallback(() => {
        familyNavSeqRef.current += 1;
    }, []);
    // Which item the drawer is showing right now; Undo closures compare against it so a
    // revert of the *previous* item (after auto-advance) never rewrites the current draft.
    const currentItemIdRef = useRef<string | null>(null);
    // Set while the discard-confirm dialog is guarding a navigation instead of a close.
    const pendingNavigationIdRef = useRef<string | null>(null);
    const pendingRouteRef = useRef<(() => void) | null>(null);

    // Panic Gap / Decision Guide state
    const [decisionGuide, setDecisionGuide] = useState<DecisionGuide | null>(null);
    const [scamRisk, setScamRisk] = useState<ScamRisk | null>(null);
    const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
    const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
    const [noticeData, setNoticeData] = useState<NoticeData | null>(null);
    const [extractionFields, setExtractionFields] = useState<ExtractionFields | null>(null);
    const [reviewRequired, setReviewRequired] = useState(false);
    const [reviewReasons, setReviewReasons] = useState<string[]>([]);
    const [scamExpanded, setScamExpanded] = useState(false);
    const [enrichRerunBusy, setEnrichRerunBusy] = useState(false);
    const [decisionGuideFetchStatus, setDecisionGuideFetchStatus] = useState<
        'inactive' | 'loading' | 'success' | 'error'
    >('inactive');
    const [documentIntelRetryToken, setDocumentIntelRetryToken] = useState(0);

    // ── Facet state ─────────────────────────────────────────────────────
    const [currentFacet, setCurrentFacet] = useState<Facet>('work');
    const [sourceHomePanel, setSourceHomePanel] = useState<SourceHomePanel | null>(null);
    const facetOverrideRef = useRef(false);
    // Facet a newly opened item lands on; set during render once the next step is known.
    const openingFacetRef = useRef<Facet>('work');
    const prevItemIdRef = useRef<string | null>(null);

    const contentScrollRef = useRef<HTMLDivElement | null>(null);
    const drawerPanelRef = useRef<HTMLDivElement | null>(null);
    const documentLoadSeqRef = useRef(0);
    const sourceDocCheckSeqRef = useRef(0);
    const lastShownPromotionFailureRef = useRef<string | null>(null);
    const quickUpdateInFlightRef = useRef(false);
    const teachStripRef = useRef<HTMLDivElement | null>(null);

    const buildDraftFromItem = (src: WorkItem): WorkItemUpdate => ({
        type: src.type || 'INBOX',
        domain: src.domain || 'general',
        state: src.state || 'TRIAGE',
        needs_action: src.needs_action ?? false,
        title: src.title || '',
        summary: src.summary || '',
        due_date: src.due_date ?? null,
        snoozed_until: src.snoozed_until ?? null,
        event_date: src.event_date ?? null,
        event_type: src.event_type ?? null,
        prep_days_override: src.prep_days_override ?? null,
        tags: src.tags ?? [],
        meta: src.meta ?? {},
        company: src.company,
        job_title: src.job_title,
        status: src.status,
        notes: src.notes,
        confidence_score: src.confidence_score,
        document_id: src.document_id ?? undefined,
    });

    const itemIdForDraft = item?.id ?? null;
    useEffect(() => {
        currentItemIdRef.current = itemIdForDraft;
    }, [itemIdForDraft]);

    const toggleAutoAdvance = () => {
        setAutoAdvancePref((prev) => {
            const next: DrawerAutoAdvancePref = prev === 'off' ? 'triage' : 'off';
            try {
                window.localStorage?.setItem(DRAWER_AUTO_ADVANCE_STORAGE_KEY, next);
            } catch {
                // Preference is a convenience; in-memory state still applies for this session.
            }
            return next;
        });
    };

    useEffect(() => {
        if (item) {
            setDraft(buildDraftFromItem(item));
            setShowEventDate(!!item.event_date);
        } else if (open) {
            const baseDraft: WorkItemUpdate = {
                type: 'TASK',
                // Read when the drawer opens, not a dependency: changing the host's area later
                // must not reset a draft the person is typing.
                domain: newItemDomain || bootstrap?.active_domain_key || 'general',
                state: 'ACTIVE',
                needs_action: true,
                title: '',
                summary: '',
                due_date: formatLocalDrawerDate(new Date()) + 'T00:00:00',
                snoozed_until: null,
                tags: [],
                meta: {},
            };
            const nextDraft = { ...baseDraft, ...(initialDraft || {}) };
            newDraftBaselineRef.current = nextDraft;
            setDraft(nextDraft);
            setShowEventDate(Boolean(nextDraft.event_date));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemIdForDraft, open, bootstrap?.active_domain_key, initialDraft]);

    useEffect(() => {
        // Prefer the currently selected item's identifiers to avoid transient
        // bleed-over from the previous item's draft during rapid drawer switches.
        const docId = item ? (item.document_id || null) : (draft.document_id || null);
        const fallbackMeta = (item ? (item.meta || {}) : (draft.meta || {})) as Record<string, any>;
        const fallbackInvoice = fallbackMeta.invoice_data || null;
        const fallbackContact = fallbackMeta.contact_info || null;
        const fallbackNotice = fallbackMeta.notice_data || null;
        const fallbackFields = fallbackMeta.fields || null;
        const fallbackReviewRequired = Boolean(fallbackMeta.requires_human_review);
        const fallbackReviewReasons = Array.isArray(fallbackMeta.review_reasons) ? fallbackMeta.review_reasons : [];
        const loadSeq = ++documentLoadSeqRef.current;
        let cancelled = false;

        const applyFallbackFromMeta = () => {
            setDecisionGuide(null);
            setScamRisk(null);
            setInvoiceData(fallbackInvoice);
            setContactInfo(fallbackContact);
            setNoticeData(fallbackNotice);
            setExtractionFields(fallbackFields);
            setReviewRequired(fallbackReviewRequired);
            setReviewReasons(fallbackReviewReasons);
            setScamExpanded(false);
        };

        if (open && docId) {
            // Clear stale panel state immediately while loading the new document.
            setDecisionGuideFetchStatus('loading');
            applyFallbackFromMeta();
            documentsApi.getDocument(docId)
                .then(doc => {
                    if (cancelled || documentLoadSeqRef.current !== loadSeq) return;
                    const extracted = doc.extracted_json || {};
                    if (extracted && extracted.decision_guide) {
                        setDecisionGuide(extracted.decision_guide as DecisionGuide);
                    } else {
                        setDecisionGuide(null);
                    }
                    if (extracted && extracted.payment_request && extracted.scam_risk) {
                        const risk = extracted.scam_risk as ScamRisk;
                        setScamRisk(risk);
                        setScamExpanded(risk.level === 'high');
                    } else {
                        setScamRisk(null);
                        setScamExpanded(false);
                    }
                    setInvoiceData(extracted.invoice_data || fallbackInvoice);
                    setContactInfo(extracted.contact_info || fallbackContact);
                    setNoticeData(extracted.notice_data || fallbackNotice);
                    setExtractionFields(extracted.fields || fallbackFields);
                    setReviewRequired(Boolean(extracted.requires_human_review) || fallbackReviewRequired);
                    setReviewReasons(Array.isArray(extracted.review_reasons) ? extracted.review_reasons : fallbackReviewReasons);
                    setDecisionGuideFetchStatus('success');
                })
                .catch(err => {
                    if (cancelled || documentLoadSeqRef.current !== loadSeq) return;
                    console.error("Failed to load decision guide:", err);
                    applyFallbackFromMeta();
                    setDecisionGuideFetchStatus('error');
                });
        } else {
            setDecisionGuideFetchStatus('inactive');
            applyFallbackFromMeta();
        }
        return () => {
            cancelled = true;
        };
    }, [open, item, draft.document_id, draft.meta, documentIntelRetryToken]);

    const retryDecisionGuideFetch = useCallback(() => {
        setDocumentIntelRetryToken((t) => t + 1);
    }, []);

    useEffect(() => {
        const docId = item?.document_id || null;
        setCheckedDocumentId(docId);
        if (!open || !docId) {
            setSourceDocumentState('none');
            setSourceDocumentMessage('');
            return;
        }

        const seq = ++sourceDocCheckSeqRef.current;
        let cancelled = false;
        setSourceDocumentState('checking');
        setSourceDocumentMessage('');

        documentsApi.getDownloadUrl(docId)
            .then(() => {
                if (cancelled || sourceDocCheckSeqRef.current !== seq) return;
                setSourceDocumentState('available');
                setSourceDocumentMessage('');
            })
            .catch((e: any) => {
                if (cancelled || sourceDocCheckSeqRef.current !== seq) return;
                const status = e?.response?.status;
                const code = e?.response?.data?.code;
                if (status === 410 || code === 'DOCUMENT_DELETED') {
                    setSourceDocumentState('deleted');
                    setSourceDocumentMessage("This file was removed and can no longer be opened.");
                    return;
                }
                if (status === 404 || code === 'DOCUMENT_MISSING') {
                    setSourceDocumentState('missing');
                    setSourceDocumentMessage("This file was removed or can't be found.");
                    return;
                }
                setSourceDocumentState('unknown_error');
                setSourceDocumentMessage('Unable to verify source document right now.');
            });

        return () => {
            cancelled = true;
        };
    }, [open, item?.document_id]);

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        domainsApi.getDomains()
            .then(async (d) => {
                const currentDomainKey = (item?.domain || '').trim();
                let nextDomains = d;
                const hasCurrent = currentDomainKey
                    ? d.some((domain) => (domain.key || '').trim() === currentDomainKey)
                    : true;
                if (currentDomainKey && !hasCurrent) {
                    try {
                        const currentDomain = await domainsApi.getDomain(currentDomainKey);
                        nextDomains = [...d, currentDomain];
                    } catch {
                        nextDomains = d;
                    }
                }
                if (mounted) setDomainOptions(nextDomains);
            })
            .catch(() => {
                if (mounted) setDomainOptions([]);
            });
        return () => {
            mounted = false;
        };
    }, [open, item?.domain]);

    useEffect(() => {
        if (!open) {
            setIsCompactHeader(false);
            setEmailViewerExpanded(false);
            setActionsMenuAnchorEl(null);
            facetOverrideRef.current = false;
            prevItemIdRef.current = null;
            lastShownPromotionFailureRef.current = null;
            setTrustDetailsExpanded(false);
            return;
        }
        setIsCompactHeader(false);
        setEmailViewerExpanded(false);
        setActionsMenuAnchorEl(null);
        facetOverrideRef.current = false;
        prevItemIdRef.current = null;
        lastShownPromotionFailureRef.current = null;
        setTrustDetailsExpanded(false);
        if (contentScrollRef.current) {
            contentScrollRef.current.scrollTop = 0;
        }
    }, [open, item?.id]);

    // An async document response must not move the user to another tab.
    useEffect(() => {
        if (!open) return;
        setCurrentFacet(openingFacetRef.current);
    }, [open, item?.id]);

    // ── Evidence tab exists only when it has something to show (no dead rooms) ──
    const hasIntel = useMemo(() => hasIntelContent({
        decisionGuideFetchStatus,
        decisionGuide,
        scamRisk,
        invoiceData,
        contactInfo,
        noticeData,
        extractionFields,
        reviewRequired,
        reviewReasons,
    }), [
        decisionGuideFetchStatus,
        decisionGuide,
        scamRisk,
        invoiceData,
        contactInfo,
        noticeData,
        extractionFields,
        reviewRequired,
        reviewReasons,
    ]);
    const visibleFacetTabs = useMemo(
        () => FACET_TABS.filter((tab) => tab.facet !== 'intel' || hasIntel),
        [hasIntel],
    );

    // If Evidence disappears under the user (e.g. navigated to an email item), fall back to the auto rule.
    useEffect(() => {
        if (!open || currentFacet !== 'intel' || hasIntel) return;
        setCurrentFacet(openingFacetRef.current);
    }, [open, currentFacet, hasIntel, item?.id]);

    useEffect(() => {
        setEmailViewerExpanded(false);
    }, [currentFacet, sourceHomePanel]);

    // On the body element itself: an effect would run before the drawer's portal mounts
    // the body and never attach. Enter compact past the threshold, leave it only back at
    // the top: the title shrinking makes the body taller, which must not flip it straight back.
    const handleBodyScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const top = event.currentTarget.scrollTop;
        setIsCompactHeader((prev) => {
            const nextCompact = prev ? top > 1 : top > compactHeaderScrollThreshold;
            return prev === nextCompact ? prev : nextCompact;
        });
    };

    const handleCopy = async (value: string) => {
        if (!value) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = value;
                textarea.style.position = 'fixed';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setToastSeverity('success');
            setToastMessage('Copied to clipboard.');
            setToastOpen(true);
        } catch (err) {
            console.error('Copy failed', err);
            setToastSeverity('error');
            setToastMessage('Copy failed.');
            setToastOpen(true);
        }
    };

    const toggleScamDetails = () => {
        if (!scamRisk || scamRisk.level === 'high') return;
        setScamExpanded((prev) => !prev);
    };

    const selectFacet = (facet: Facet) => {
        facetOverrideRef.current = true;
        setCurrentFacet(facet);
    };

    const toggleSourceHomePanel = (panel: SourceHomePanel) => {
        setSourceHomePanel((current) => (current === panel ? null : panel));
    };

    // ARIA tabs pattern: roving tabindex with arrow-key movement (selection follows focus).
    // Wraps over the *visible* tabs so a hidden Evidence tab is never a keyboard stop.
    const handleTabListKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        const tabs = visibleFacetTabs;
        const currentIndex = Math.max(0, tabs.findIndex((tab) => tab.facet === currentFacet));
        let nextIndex: number;
        switch (event.key) {
            case 'ArrowRight':
                nextIndex = (currentIndex + 1) % tabs.length;
                break;
            case 'ArrowLeft':
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = tabs.length - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        const nextFacet = tabs[nextIndex].facet;
        // Commit the selected tab before focusing so End/Home survive the header/facet remount.
        flushSync(() => selectFacet(nextFacet));
        document.getElementById(`wb-drawer-tab-${nextFacet}`)?.focus();
    };

    const domainMenu = (() => {
        const base: Record<string, DomainMenuItem> = {
            general: { key: 'general', label: 'General', icon: '🌍' },
            job_hunt: { key: 'job_hunt', label: 'Job Hunt', icon: '💼' },
            finance: { key: 'finance', label: 'Finance', icon: '💰' },
            parenthood: { key: 'parenthood', label: 'Parenthood', icon: '🧸' },
            business: { key: 'business', label: 'Business', icon: '🏢' },
            personal: { key: 'personal', label: 'Personal', icon: '👤' },
            health: { key: 'health', label: 'Health', icon: '🏥' },
        };

        for (const d of domainOptions) {
            const key = (d.key || '').trim();
            if (!key) continue;
            const retired = d.lifecycle_status === 'archived' || d.lifecycle_status === 'deactivated';
            base[key] = {
                key,
                label: (d.label || base[key]?.label || key).trim(),
                icon: (d.icon || base[key]?.icon || '✨').trim(),
                disabled: retired,
                statusLabel: d.lifecycle_status === 'archived'
                    ? 'archived'
                    : retired
                        ? 'inactive'
                        : undefined,
            };
        }

        const baseOrder = ['general', 'job_hunt', 'finance', 'parenthood', 'business', 'personal', 'health'];
        const baseItems = baseOrder.map((k) => base[k]).filter(Boolean);
        const extraItems = Object.values(base).filter((x) => !baseOrder.includes(x.key));
        extraItems.sort((a, b) => a.label.localeCompare(b.label));
        return [...baseItems, ...extraItems];
    })();



    const normalizeDomainKey = (value?: string | null) => {
        const raw = (value || 'general').toString().trim().toLowerCase();
        if (!raw) return 'general';
        return raw.replace(/[\s-]+/g, '_');
    };

    const activeDomainKey = normalizeDomainKey(draft.domain);
    const isJobDomain = activeDomainKey === 'job_hunt';

    const activeDomainConfig = useMemo(() => {
        if (!domainOptions.length) return null;
        return domainOptions.find((d) => normalizeDomainKey(d.key) === activeDomainKey) || null;
    }, [domainOptions, activeDomainKey]);

    const drawerDomainLabel = useMemo(() => {
        const domainKey = (item?.domain || draft.domain || 'general').trim();
        if (!domainKey) return 'general';
        const match = domainOptions.find(
            (domain) => normalizeDomainKey(domain.key) === normalizeDomainKey(domainKey),
        );
        return match?.label || domainKey;
    }, [item?.domain, draft.domain, domainOptions]);

    const fieldConfig = useMemo(() => {
        const configured = activeDomainConfig?.field_config;
        if (configured && Object.keys(configured).length > 0) {
            return configured;
        }
        return DEFAULT_DOMAIN_FIELDS[activeDomainKey] || {};
    }, [activeDomainConfig, activeDomainKey]);

    const domainFieldEntries = useMemo(() => {
        const meta = draft.meta || {};
        const hiddenKeys = isJobDomain ? new Set(['company', 'status', 'job_title']) : new Set<string>();

        const formatValue = (value: unknown, key?: string) => {
            if (value === null || value === undefined) return null;
            if ((key === 'due_date' || key === 'event_date') && typeof value === 'string') {
                const normalized = toDateInputValue(value);
                return normalized || null;
            }
            if (typeof value === 'string') {
                const trimmed = value.trim();
                return trimmed ? trimmed : null;
            }
            if (typeof value === 'number') return String(value);
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (Array.isArray(value)) {
                const compact = value.map((entry) => String(entry).trim()).filter(Boolean);
                return compact.length ? compact.join(', ') : null;
            }
            return null;
        };

        const resolveFieldValue = (fieldKey: string) => {
            const key = fieldKey.trim();
            const metaValue = formatValue((meta as Record<string, unknown>)[key], key);
            if (metaValue) return metaValue;

            let fallback: unknown;
            switch (key) {
                case 'company':
                case 'entity':
                    fallback = draft.company;
                    break;
                case 'job_title':
                    fallback = draft.job_title;
                    break;
                case 'status':
                    fallback = draft.status;
                    break;
                case 'event_type':
                    fallback = draft.event_type;
                    break;
                case 'event_date':
                    fallback = draft.event_date;
                    break;
                case 'due_date':
                    fallback = draft.due_date;
                    break;
                case 'requires_action':
                    fallback = draft.needs_action;
                    break;
                default:
                    fallback = undefined;
            }
            return formatValue(fallback, key);
        };

        return Object.entries(fieldConfig)
            .filter(([key, label]) => key && label && !hiddenKeys.has(key))
            .map(([key, label]) => {
                const value = resolveFieldValue(key);
                if (!value) return null;
                return { key, label: String(label), value };
            })
            .filter((entry): entry is { key: string; label: string; value: string } => Boolean(entry));
    }, [fieldConfig, draft, isJobDomain]);

    // Track whether Save-path / facet fields differ from server. Quick actions auto-save
    // (state, needs_action, snoozed_until) and are excluded — see draftFacetSaveDiffersFromItem.
    const isDirty = useMemo(() => {
        if (!item) return JSON.stringify(draft) !== JSON.stringify(newDraftBaselineRef.current);
        return draftFacetSaveDiffersFromItem(draft, item);
    }, [item, draft]);
    dirtyRef.current = isDirty;
    const hasActiveSnooze = useMemo(() => {
        const sourceDate = draft.snoozed_until === undefined
            ? (item?.snoozed_until ?? null)
            : draft.snoozed_until;
        if (!sourceDate) return false;
        const parsed = new Date(sourceDate);
        if (Number.isNaN(parsed.getTime())) return false;
        return parsed > new Date();
    }, [draft.snoozed_until, item?.snoozed_until]);
    const actionsMenuOpen = Boolean(actionsMenuAnchorEl);

    const entitlementsRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scheduleWorkbenchEntitlementsRefresh = useCallback(() => {
        if (entitlementsRefreshDebounceRef.current) {
            clearTimeout(entitlementsRefreshDebounceRef.current);
        }
        entitlementsRefreshDebounceRef.current = setTimeout(() => {
            entitlementsRefreshDebounceRef.current = null;
            void refreshBootstrap();
        }, 400);
    }, [refreshBootstrap]);

    useEffect(() => {
        if (!open) return;
        scheduleWorkbenchEntitlementsRefresh();
    }, [open, item?.id, scheduleWorkbenchEntitlementsRefresh]);

    useEffect(() => {
        if (!actionsMenuOpen) return;
        scheduleWorkbenchEntitlementsRefresh();
    }, [actionsMenuOpen, scheduleWorkbenchEntitlementsRefresh]);

    useEffect(() => {
        return () => {
            if (entitlementsRefreshDebounceRef.current) {
                clearTimeout(entitlementsRefreshDebounceRef.current);
            }
        };
    }, []);

    const currentState = (draft.state || item?.state || 'TRIAGE').toUpperCase();
    const currentStateLabel = getWorkItemStateLabel(currentState) || currentState;
    const isTriageState = currentState === 'TRIAGE';
    const isDoneState = currentState === 'DONE';
    const isArchivedState = currentState === 'ARCHIVED';

    // One header size (D4): the title shrinks only while the body is scrolled down or
    // the original email is open.
    const compactHeaderActive = isCompactHeader || emailViewerExpanded;

    const showSaveButton = !item || isDirty;
    const trustDetailsId = item ? `wb-trust-details-${item.id}` : 'wb-trust-details-new';
    const trustModel = useMemo(
        () => item ? deriveTrustStripViewModel(item, sourceDocumentState) : null,
        [item, sourceDocumentState],
    );
    const trustIsCritical = Boolean(trustModel?.isCritical);
    const trustSeenKey = item && trustModel && !trustModel.isCritical
        ? `drawer.trustDetailsSeen.${item.id}.${trustModel.state}`
        : null;

    const itemIsSourceHome = Boolean(item && isSourceHomeItem(item));
    const showSourceHome = itemIsSourceHome || viewingSource;
    // Legacy families have no source row. The overlay keeps the child loaded, so
    // child writes (snooze, archive, delete, edit) must not stay available.
    const virtualSource = viewingSource && !itemIsSourceHome;

    const familyEdgeId = item && mayHaveSourceFamily(item) ? item.id : '';
    // Ignore a family payload that still belongs to the previously open item.
    const familyResolved = familyOwnerId === familyEdgeId;
    const shownFamily = familyResolved && family?.discoverable ? family : null;
    // The open item can change state (Mark complete, Snooze) after the family
    // answered. Its row and segment read the live item, not the fetched copy.
    const familyTasks = useMemo(() => {
        if (!shownFamily || !item) return [];
        return shownFamily.tasks.map((task) => (
            task.id === item.id
                ? { ...task, state: item.state ?? task.state, snoozed_until: item.snoozed_until ?? null }
                : task
        ));
    }, [shownFamily, item]);

    useEffect(() => {
        setViewingSource(false);
        setSourceHomePanel(null);
    }, [item?.id]);

    useLayoutEffect(() => {
        const nextId = item?.id ?? null;
        const prev = familyNavWatchRef.current;
        familyNavWatchRef.current = { id: nextId, open };
        if (!prev) return;
        if (prev.id === nextId && prev.open === open) return;
        // A newer pile selection or a closed drawer wins over a family fetch
        // that was already in flight, even when this instance did not click Next.
        invalidateInFlightFamilyNav();
    }, [item?.id, open, invalidateInFlightFamilyNav]);

    useEffect(() => {
        if (!open || !familyEdgeId) {
            setFamily(null);
            setFamilyOwnerId('');
            return;
        }
        let cancelled = false;
        const requestedId = familyEdgeId;
        workItemsApi.getSourceFamily(requestedId)
            .then((next) => {
                if (cancelled) return;
                setFamilyOwnerId(requestedId);
                setFamily(next);
            })
            .catch(() => {
                if (cancelled) return;
                setFamilyOwnerId(requestedId);
                setFamily(null);
            });
        return () => {
            cancelled = true;
        };
    }, [open, familyEdgeId]);

    const sourceInfo = item ? getWorkItemSourceInfo(item) : null;
    const persistedSourceSummary = sourceSummaryText(item?.summary);
    const sourceSummary = sourceSummaryText(typeof draft.summary === 'string' ? draft.summary : item?.summary);
    // Hide the source tile on a family child while getSourceFamily is pending
    // or after it fails. Once the fetch succeeds, honor that answer: a
    // discoverable family stays step-view; an undiscoverable response (stale
    // batch metadata, one task with no home) may show the stored summary.
    const stepView = Boolean(
        item
        && !itemIsSourceHome
        && !showSourceHome
        && (familyResolved && family != null
            ? family.discoverable
            : mayHaveSourceFamily(item)),
    );
    const sourceTileKind = sourceInfo && (sourceInfo.sourceType === 'email' || sourceInfo.sourceType === 'document' || sourceInfo.sourceType === 'manual')
        ? sourceInfo.sourceType
        : null;
    const showSourceTile = Boolean(item && persistedSourceSummary && sourceTileKind && !stepView && !virtualSource);
    // Shape A source presentation: a source-home row, or the open item already
    // showing the summary tile. Not a selected step, and not the virtual overlay.
    const sourceHomeAccordion = !stepView && !virtualSource && (itemIsSourceHome || showSourceTile);
    // A source home's work is its steps, so it never offers Mark complete; the virtual
    // overlay is the source too. A solo email, document, or note is its own task
    // and keeps Mark complete on the same presentation.
    const blockComplete = itemIsSourceHome || virtualSource;
    // On that presentation Start / Snooze / Dismiss sit in the bar under the tile;
    // the header and the menu do not repeat Start or Dismiss.
    const triageVerbsInBarOnly = sourceHomeAccordion || virtualSource;
    // A new item has nothing to read yet, so it opens on Edit with no tab strip (D5).
    const showFacetTabs = Boolean(item) && !sourceHomeAccordion;

    // Resolve what the primary action is for the header
    let primaryActionLabel: string | undefined;
    let primaryActionIcon: React.ReactNode | undefined;
    let onPrimaryAction: (() => void) | undefined;
    let primaryActionColor: 'success' | 'warning' | 'primary' | 'info' = 'primary';

    if (item && isDoneState && !virtualSource) {
        primaryActionLabel = 'Reopen';
        primaryActionIcon = <FontAwesomeIcon icon={faUndo} />;
        primaryActionColor = 'warning';
        onPrimaryAction = async () => {
            await reopenItem();
        };
    } else if (blockComplete || (isTriageState && triageVerbsInBarOnly)) {
        primaryActionLabel = undefined;
        onPrimaryAction = undefined;
    } else if (item && isTriageState) {
        primaryActionLabel = 'Start';
        primaryActionIcon = <FontAwesomeIcon icon={faPlay} />;
        primaryActionColor = 'primary';
        onPrimaryAction = async () => {
            await startItem();
        };
    } else if (item && !isArchivedState) {
        primaryActionLabel = 'Mark complete';
        primaryActionIcon = <FontAwesomeIcon icon={faCheck} />;
        primaryActionColor = 'success';
        onPrimaryAction = async () => {
            // Keeps the drawer open on the completed item (with Undo) unless the
            // auto-advance preference moves on to the next one.
            await markDone();
        };
    }

    // Triage verbs live in the bar under the body, never in the header (D1).
    const headerPrimaryLabel = isTriageState && !virtualSource ? undefined : primaryActionLabel;
    // The source overlay keeps the child loaded, so its ⋮ offers only the document (D6).
    const showActionsMenu = Boolean(item) && (!virtualSource || Boolean(item?.document_id));

    const handleClose = () => {
        if (saving || quickUpdateInFlightRef.current) {
            showToast('An update is still in progress.', 'info', toastUndo || undefined);
            return;
        }
        // Next and close are ordinary navigation. A family fetch still in flight
        // must not switch or reopen the drawer after the person has moved on.
        invalidateInFlightFamilyNav();
        if (isDirty) {
            pendingRouteRef.current = null;
            pendingNavigationIdRef.current = null;
            setConfirmDiscard(true);
        } else {
            onClose();
        }
    };
    const selectNavigationTarget = (targetId: string) => {
        if (!navigation) return;
        if (!navigation.onSelectId(targetId)) {
            showToast('All caught up.', 'success');
            onClose();
        }
    };
    /** Prev/next from buttons or keys. Unsaved edits route through the discard confirm. */
    const navigateTo = (targetId: string | null) => {
        if (!navigation || !targetId) return;
        if (saving || quickUpdateInFlightRef.current) {
            showToast('An update is still in progress.', 'info', toastUndo || undefined);
            return;
        }
        invalidateInFlightFamilyNav();
        if (isDirty) {
            pendingRouteRef.current = null;
            pendingNavigationIdRef.current = targetId;
            setConfirmDiscard(true);
            return;
        }
        selectNavigationTarget(targetId);
    };
    const cancelDiscard = () => {
        pendingRouteRef.current = null;
        pendingNavigationIdRef.current = null;
        setConfirmDiscard(false);
    };
    const discardAndContinue = () => {
        if (item) setDraft(buildDraftFromItem(item));
        forceClose();
    };
    const forceClose = () => {
        const pendingRoute = pendingRouteRef.current;
        pendingRouteRef.current = null;
        const pendingTarget = pendingNavigationIdRef.current;
        pendingNavigationIdRef.current = null;
        setConfirmDiscard(false);
        if (pendingRoute) {
            pendingRoute();
            return;
        }
        if (pendingTarget) {
            // Discarding edits in order to move on: the draft rebuilds from the next item.
            selectNavigationTarget(pendingTarget);
            return;
        }
        onClose();
    };
    const pileNavigationHidden = viewingSource && !itemIsSourceHome;
    const showNavigation = Boolean(navigation && navigation.total > 1) && !pileNavigationHidden;

    const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!item || saving || confirmDiscard || confirmDelete || confirmRerunEnrichment) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (actionsMenuOpen || isEditableKeyTarget(event.target)) return;

        switch (event.key) {
            case 'j':
            case 'ArrowDown':
                if (!showNavigation) return;
                event.preventDefault();
                navigateTo(navigation?.nextId ?? null);
                return;
            case 'k':
            case 'ArrowUp':
                if (!showNavigation) return;
                event.preventDefault();
                navigateTo(navigation?.prevId ?? null);
                return;
            case 'd':
                if (!onPrimaryAction) return;
                event.preventDefault();
                onPrimaryAction();
                return;
            case 'e':
                if (virtualSource) return;
                event.preventDefault();
                void toggleArchive();
                return;
            case 's':
                if (virtualSource || isArchivedState || isDoneState) return;
                event.preventDefault();
                void snooze(1);
                return;
            default:
                return;
        }
    };
    const openActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setActionsMenuAnchorEl(event.currentTarget);
    };
    const closeActionsMenu = () => {
        setActionsMenuAnchorEl(null);
    };

    const getTitle = (item: WorkItem) => getWorkItemDisplayTitle(item);
    const sourceChip = sourceInfo ? sourceKindChipLabel(sourceInfo.sourceType) : null;
    const lifeAreaChip = lifeAreaChipLabel(drawerDomainLabel);
    const dueYmd = extractYmdFromDue(item?.due_date || draft.due_date);
    const duePhrase = dueYmd
        ? dueBadgeFromYmd(dueYmd, calendarYmdInZone(new Date(), effectiveUserTimeZone(bootstrap?.user?.timezone)))
        : null;
    // A future snooze replaces the state word on open work (D3); the draft holds a
    // snooze or unsnooze that has not come back from the server yet.
    const headerStatusLabel = drawerStatusLabel({
        state: currentState,
        snoozed_until: draft.snoozed_until === undefined ? (item?.snoozed_until ?? null) : draft.snoozed_until,
    }) || currentStateLabel;
    const headerDueStatus = item && !virtualSource ? dueStatusLine(duePhrase, headerStatusLabel) : null;
    const senderLine = readSenderFromNotes(typeof draft.notes === 'string' ? draft.notes : item?.notes);
    const documentFilename = item && item.meta && typeof item.meta === 'object'
        ? (typeof (item.meta as Record<string, unknown>).source_document_filename === 'string'
            ? (item.meta as Record<string, unknown>).source_document_filename as string
            : null)
        : null;
    const provenanceFact = item
        ? quietProvenanceFact({
            createdFrom: getWorkItemCreatedFromLabel(item),
            sender: senderLine,
            documentFilename,
        })
        : null;
    const derivedTasksText = shownFamily?.discoverable && (shownFamily.source_kind === 'email' || shownFamily.source_kind === 'document')
        ? formatDerivedTasks(shownFamily.source_kind, shownFamily.tasks.length)
        : null;
    const titleOverride = viewingSource && !itemIsSourceHome && shownFamily?.source_title
        ? shownFamily.source_title
        : null;

    const commitFamilyArrival = (seq: number, arrive: () => void) => {
        if (seq !== familyNavSeqRef.current) return;
        // Dirty is checked again here. The confirm before the fetch misses an
        // edit typed while getWorkItem is still in flight.
        if (dirtyRef.current) {
            pendingNavigationIdRef.current = null;
            pendingRouteRef.current = () => {
                if (seq !== familyNavSeqRef.current) return;
                arrive();
            };
            setConfirmDiscard(true);
            return;
        }
        arrive();
    };

    const openFamilyTask = (taskId: string) => {
        if (!item) return;
        const seq = ++familyNavSeqRef.current;
        if (taskId === item.id) {
            setViewingSource(false);
            return;
        }
        beginFamilyJump(async () => {
            try {
                const next = await workItemsApi.getWorkItem(taskId);
                commitFamilyArrival(seq, () => {
                    setViewingSource(false);
                    onOpenFamilyMember?.(next);
                });
            } catch {
                if (seq !== familyNavSeqRef.current) return;
                showToast('Could not open that next step.', 'error');
            }
        });
    };

    const openSourceHome = () => {
        if (!shownFamily?.discoverable || !item) return;
        const seq = ++familyNavSeqRef.current;
        if (isSourceHomeItem(item)) {
            setViewingSource(false);
            return;
        }
        const homeId = shownFamily.source_home_id;
        if (homeId && homeId !== item.id) {
            beginFamilyJump(async () => {
                try {
                    const next = await workItemsApi.getWorkItem(homeId);
                    commitFamilyArrival(seq, () => {
                        onOpenFamilyMember?.(next);
                    });
                } catch {
                    if (seq !== familyNavSeqRef.current) return;
                    showToast('Could not open the source.', 'error');
                }
            });
            return;
        }
        beginFamilyJump(() => {
            if (seq !== familyNavSeqRef.current) return;
            setActionsMenuAnchorEl(null);
            setConfirmDelete(false);
            setViewingSource(true);
        });
    };

    const beginFamilyJump = (run: () => void | Promise<void>) => {
        if (saving || quickUpdateInFlightRef.current) {
            showToast('An update is still in progress.', 'info', toastUndo || undefined);
            return;
        }
        if (isDirty) {
            pendingNavigationIdRef.current = null;
            pendingRouteRef.current = () => { void run(); };
            setConfirmDiscard(true);
            return;
        }
        void run();
    };

    const showToast = (
        message: string,
        severity: 'success' | 'error' | 'info' | 'warning' = 'success',
        undo?: () => void | Promise<void>,
    ) => {
        setToastMessage(message);
        setToastSeverity(severity);
        setToastUndo(undo ? () => undo : null);
        setToastOpen(true);
    };

    const hideToast = (reason?: string) => {
        if (reason === 'clickaway') return;
        setToastOpen(false);
        setToastUndo(null);
    };

    const handleToastUndo = () => {
        const undo = toastUndo;
        setToastOpen(false);
        setToastUndo(null);
        if (undo) void undo();
    };

    const stopSimilarItemPoll = useCallback(() => {
        if (similarItemPollRef.current) {
            clearInterval(similarItemPollRef.current);
            similarItemPollRef.current = null;
        }
    }, []);

    const startSimilarItemPoll = useCallback((anchorWorkItemId: string) => {
        if (bootstrap?.capabilities?.similar_item_suggestions_enabled !== true) {
            return;
        }
        stopSimilarItemPoll();
        const startedAt = Date.now();
        const poll = async () => {
            try {
                const response = await fetchSimilarItemSuggestions();
                const match = response.suggestions.find(
                    (suggestion) => suggestion.anchor_work_item_id === anchorWorkItemId,
                );
                if (match) {
                    stopSimilarItemPoll();
                    setSimilarItemReadyBatchId(match.batch_id);
                    setSimilarItemStatusMessage('Similar items are ready for review.');
                    setSimilarItemSnackbarOpen(true);
                } else if (Date.now() - startedAt > 30000) {
                    stopSimilarItemPoll();
                }
            } catch {
                if (Date.now() - startedAt > 30000) {
                    stopSimilarItemPoll();
                }
            }
        };
        void poll();
        similarItemPollRef.current = setInterval(() => {
            void poll();
        }, 2000);
    }, [bootstrap?.capabilities?.similar_item_suggestions_enabled, stopSimilarItemPoll]);

    useEffect(() => () => stopSimilarItemPoll(), [stopSimilarItemPoll]);

    const toggleTrustDetails = () => {
        setTrustDetailsExpanded((prev) => {
            const next = !prev;
            if (next && trustSeenKey) {
                try {
                    window.sessionStorage?.setItem(trustSeenKey, '1');
                } catch {
                    // Session storage is a convenience only; disclosure behavior remains local.
                }
            }
            return next;
        });
    };

    const itemId = item?.id;

    useEffect(() => {
        if (!open || !itemId || trustIsCritical || !trustSeenKey) return;

        let seen = false;
        try {
            seen = window.sessionStorage?.getItem(trustSeenKey) === '1';
        } catch {
            seen = false;
        }
        if (seen) {
            setTrustDetailsExpanded(false);
        }
    }, [open, itemId, trustIsCritical, trustSeenKey]);

    const notifySourceHomeCue = (previous: WorkItem, next: WorkItem) => {
        if (previous.due_date === next.due_date && (previous.state || '') === (next.state || '')) {
            return;
        }
        if (isSourceHomeItem(previous) || isSourceHomeItem(next)) return;
        // Tie the refresh to the item that just changed, not the drawer’s current
        // family. Auto-advance or a still-pending /family fetch would otherwise
        // skip the cue or fetch the wrong home.
        const knownFamily = familyRef.current;
        const knownHomeId = knownFamily
            && (knownFamily.source_home_id === previous.id
                || knownFamily.tasks.some((task) => task.id === previous.id))
            ? knownFamily.source_home_id
            : null;

        const refresh = async () => {
            let homeId = knownHomeId;
            if (!homeId && mayHaveSourceFamily(previous)) {
                try {
                    const fetched = await workItemsApi.getSourceFamily(previous.id);
                    homeId = fetched.discoverable ? fetched.source_home_id : null;
                } catch {
                    return;
                }
            }
            if (!homeId || homeId === next.id || homeId === previous.id) return;
            try {
                onUpdate(await workItemsApi.getWorkItem(homeId));
            } catch {
                // Child already saved; the home cue catches up on the next list load.
            }
        };
        void refresh();
    };

    const save = async (afterSave?: () => void) => {
        if (saving || quickUpdateInFlightRef.current) return;
        const continueAfterSave = typeof afterSave === 'function';
        const finish = afterSave ?? onClose;
        setSaving(true);
        try {
            if (item) {
                const priorDomain = item.domain || 'general';
                const updated = await workItemsApi.updateWorkItem(item.id, draft);
                const pe = (updated.meta as Record<string, unknown> | undefined)?.promotion_enrichment as
                    | { status?: string; message?: string; updated_at?: string }
                    | undefined;
                setDraft(buildDraftFromItem(updated));
                onUpdate(updated);
                notifySourceHomeCue(item, updated);
                const newDomain = updated.domain || 'general';
                const domainChanged = priorDomain !== newDomain;
                if (pe?.status === 'failed' && typeof pe.message === 'string') {
                    const signalKey = typeof pe.updated_at === 'string' && pe.updated_at.trim()
                        ? pe.updated_at
                        : pe.message;
                    if (lastShownPromotionFailureRef.current !== signalKey) {
                        lastShownPromotionFailureRef.current = signalKey;
                        showToast(pe.message, 'info');
                    }
                    if (domainChanged) {
                        startSimilarItemPoll(updated.id);
                    }
                    if (continueAfterSave) {
                        finish();
                    }
                    return;
                }
                if (domainChanged) {
                    startSimilarItemPoll(updated.id);
                }
            } else {
                if (!draft.title) {
                    showToast('Title is required', 'error');
                    setSaving(false);
                    return;
                }
                const created = await workItemsApi.createWorkItem(draft);
                onUpdate(created);
            }
            finish();
        } catch (e: any) {
            console.error('Failed to save:', e);
            showToast('Failed to save: ' + (e.response?.data?.detail || e.message), 'error', toastUndo || undefined);
        } finally {
            setSaving(false);
        }
    };

    const handleFieldChange = (field: keyof WorkItemUpdate, value: any) => {
        setDraft((prev) => ({ ...prev, [field]: value }));
    };

    // Detect event type from title and summary (client-side keyword matching)
    const detectEventType = (title: string, summary?: string | null): string | null => {
        const text = (title + ' ' + (summary || '')).toLowerCase();

        const keywords: Record<string, { keywords: string[], priority: number }> = {
            "interview": { keywords: ["interview", "interview round", "phone screen", "technical interview", "onsite", "on-site", "final round"], priority: 10 },
            "exam": { keywords: ["exam", "test", "quiz", "certification", "assessment test"], priority: 9 },
            "presentation": { keywords: ["presentation", "present", "demo", "pitch", "showcase"], priority: 8 },
            "flight": { keywords: ["flight", "travel", "trip", "plane", "airport", "fly to"], priority: 7 },
            "appointment": { keywords: ["appointment", "doctor", "dentist", "checkup", "visit"], priority: 6 },
            "meeting": { keywords: ["meeting", "call", "standup", "sync", "conference", "discussion"], priority: 5 },
            "deadline": { keywords: ["due", "submit", "deadline", "deliver", "pay", "renew", "file", "send by", "must be"], priority: 4 }
        };

        const matches: [string, number][] = [];
        for (const [eventType, config] of Object.entries(keywords)) {
            for (const keyword of config.keywords) {
                if (text.includes(keyword)) {
                    matches.push([eventType, config.priority]);
                    break;
                }
            }
        }

        if (matches.length > 0) {
            matches.sort((a, b) => b[1] - a[1]);
            return matches[0][0];
        }
        return null;
    };

    // Calculate prep days
    const calculatePrepDays = (eventType: string | null, override: number | null): number => {
        if (override !== null && override !== undefined) return override;
        if (eventType && eventType in DEFAULT_PREP_TIMES) return DEFAULT_PREP_TIMES[eventType];
        return DEFAULT_PREP_TIMES.default;
    };

    // Calculate suggested action date
    useEffect(() => {
        if (draft.event_date && item) {
            const eventDate = parseDateForDrawerMath(draft.event_date);
            if (!eventDate) {
                setSuggestedActionDate(null);
                setPrepDaysUsed(null);
                setDetectedEventType(null);
                return;
            }
            const title = draft.title || item.title || '';
            const summary = draft.summary || item.summary;

            // Detect or use explicit event type
            const eventType = draft.event_type || detectEventType(title, summary);
            setDetectedEventType(eventType);

            // Calculate prep days
            const prepDays = calculatePrepDays(eventType, draft.prep_days_override ?? null);
            setPrepDaysUsed(prepDays);

            // Calculate suggestion
            const actionDate = new Date(eventDate);
            actionDate.setDate(actionDate.getDate() - prepDays);
            setSuggestedActionDate(formatLocalDrawerDate(actionDate) + 'T00:00:00');
        } else {
            setSuggestedActionDate(null);
            setPrepDaysUsed(null);
            setDetectedEventType(null);
        }
    }, [draft.event_date, draft.event_type, draft.prep_days_override, draft.title, draft.summary, item]);

    const applySuggestion = () => {
        if (suggestedActionDate) {
            handleFieldChange('due_date', suggestedActionDate);
            showToast(`Unsaved action date: ${prepDaysUsed} day${prepDaysUsed !== 1 ? 's' : ''} before event`, 'info');
        }
    };

    const startOnEventDay = () => {
        if (draft.event_date) {
            handleFieldChange('due_date', draft.event_date);
            showToast('Unsaved action date: event day', 'info');
        }
    };

    // Auto-save on blur for text fields could be an option, but sticking to explicit save for now or implicit updates
    // For quick actions we do immediate updates
    interface QuickUpdateOptions {
        successMessage?: string;
        /** Offer an Undo action in the success toast that restores the pre-action values. */
        undoable?: boolean;
        /**
         * Triage verb: after success, move to the next item when the host provides
         * navigation, the preference allows it, and there are no unsaved edits.
         */
        advance?: boolean;
    }
    const shouldAutoAdvance = (wasTriage: boolean): boolean => {
        if (!navigation || autoAdvancePref === 'off' || isDirty) return false;
        return autoAdvancePref === 'always' || wasTriage;
    };
    const getPendingDraftEdits = (
        currentDraft: WorkItemUpdate,
        baselineDraft: WorkItemUpdate,
    ): Partial<WorkItemUpdate> => (
        Object.entries(currentDraft).reduce<Partial<WorkItemUpdate>>(
            (pending, [key, value]) => {
                const draftKey = key as keyof WorkItemUpdate;
                const baselineValue = (baselineDraft as Record<string, unknown>)[key];
                if (JSON.stringify(value) !== JSON.stringify(baselineValue)) {
                    (pending as Record<string, unknown>)[draftKey] = value;
                }
                return pending;
            },
            {},
        )
    );
    const quickUpdate = async (updates: WorkItemUpdate, options?: QuickUpdateOptions) => {
        if (!item || saving || quickUpdateInFlightRef.current) return;
        quickUpdateInFlightRef.current = true;
        setSaving(true);
        try {
            // A quick action is deliberately independent from explicit form saving.
            // Keep the user's unsaved field edits in the local draft and PATCH only
            // the action fields so "Mark complete" cannot silently save a Title edit.
            const priorDraft = buildDraftFromItem(item);
            const priorValues: Partial<WorkItemUpdate> = {};
            (Object.keys(updates) as Array<keyof WorkItemUpdate>).forEach((key) => {
                if (key === 'meta') {
                    const previousMeta = (priorDraft.meta || {}) as Record<string, unknown>;
                    const restoreMeta: Record<string, unknown> = {};
                    const clearMeta: string[] = [];
                    Object.keys(updates.meta || {}).forEach((metaKey) => {
                        if (Object.prototype.hasOwnProperty.call(previousMeta, metaKey)) {
                            restoreMeta[metaKey] = previousMeta[metaKey];
                        } else {
                            clearMeta.push(metaKey);
                        }
                    });
                    if (Object.keys(restoreMeta).length > 0) {
                        priorValues.meta = restoreMeta;
                    }
                    if (clearMeta.length > 0) {
                        priorValues.meta_clear = clearMeta;
                    }
                    return;
                }
                (priorValues as Record<string, unknown>)[key] = (priorDraft as Record<string, unknown>)[key];
            });
            if (
                Object.prototype.hasOwnProperty.call(updates, 'domain')
                && isBestGuessPlacement(item)
            ) {
                priorValues.restore_best_guess = true;
            }
            // Decide the advance target *before* the PATCH: the current item may leave a
            // filtered list once its state changes, but its neighbours will not.
            const wasTriage = (item.state || '').toUpperCase() === 'TRIAGE';
            const advancing = Boolean(options?.advance) && shouldAutoAdvance(wasTriage);
            const advanceTargetId = advancing ? (navigation?.nextId ?? navigation?.prevId ?? null) : null;

            const updated = await workItemsApi.updateWorkItem(item.id, updates);
            const updatedDraft = buildDraftFromItem(updated);
            const draftActionUpdates = { ...updates };
            delete draftActionUpdates.confirm_domain_placement;
            // Metadata PATCHes are partial; the response contains the merged server metadata.
            delete draftActionUpdates.meta;
            delete draftActionUpdates.meta_clear;
            setDraft((currentDraft) => ({
                ...updatedDraft,
                ...getPendingDraftEdits(currentDraft, priorDraft),
                ...draftActionUpdates,
            }));
            onUpdate(updated);
            notifySourceHomeCue(item, updated);

            let advanced = false;
            let caughtUp = false;
            if (advancing && navigation && !dirtyRef.current) {
                if (advanceTargetId && navigation.onSelectId(advanceTargetId)) {
                    advanced = true;
                } else if (advanceTargetId) {
                    // Neighbour vanished (e.g. concurrent sync) — nothing left to triage here.
                    caughtUp = true;
                }
            }

            if (options?.successMessage) {
                const undo = options.undoable
                    ? async () => {
                        try {
                            const reverted = await workItemsApi.updateWorkItem(updated.id, {
                                ...priorValues,
                            });
                            // Only touch the draft when the reverted item is still the one on screen.
                            const draftPriors = { ...priorValues };
                            delete draftPriors.meta_clear;
                            if (currentItemIdRef.current === reverted.id) {
                                setDraft((currentDraft) => ({
                                    ...buildDraftFromItem(reverted),
                                    ...getPendingDraftEdits(currentDraft, updatedDraft),
                                    ...draftPriors,
                                    meta: reverted.meta,
                                }));
                            }
                            onUpdate(reverted);
                            notifySourceHomeCue(updated, reverted);
                            showToast('Action undone.', 'success');
                        } catch (err: any) {
                            showToast(err?.response?.data?.detail || 'Could not undo the last action.', 'error');
                        }
                    }
                    : undefined;
                showToast(
                    caughtUp ? `${options.successMessage} All caught up.` : options.successMessage,
                    'success',
                    undo,
                );
            }
            if (!advanced && !dirtyRef.current && caughtUp) {
                onClose();
            }
            return updated;
        } catch (e: any) {
            console.error('Failed quick action update:', e);
            showToast(e?.response?.data?.detail || 'Failed to apply action.', 'error');
            throw e;
        } finally {
            quickUpdateInFlightRef.current = false;
            setSaving(false);
        }
    };

    const applySourceRule = async (attention: Attention) => {
        if (!item || saving || quickUpdateInFlightRef.current) return;
        quickUpdateInFlightRef.current = true;
        setSaving(true);
        try {
            const response = await workItemsApi.putSourceRule(item.id, attention);
            const updated = response.item;
            const priorDraft = buildDraftFromItem(item);
            const updatedDraft = buildDraftFromItem(updated);
            setDraft((currentDraft) => ({
                ...updatedDraft,
                ...getPendingDraftEdits(currentDraft, priorDraft),
            }));
            onUpdate(updated, {
                source_key: response.rule.source_key,
                attention: response.rule.attention,
                applied_ids: response.applied_ids || [],
            });
            const label = sourceKeyLabel(updated.source_key || item.source_key || '');
            const messages: Record<Attention, string> = {
                watch: `${label} will stack from now on`,
                ignore: `${label} will stay out of Today`,
                action: `${label} will stay a task`,
            };
            const undo = async () => {
                try {
                    const undone = await workItemsApi.undoSourceRule(item.id, {
                        previous_attention: response.previous_attention ?? null,
                        prior_items: response.prior_items || [],
                    });
                    const restoredOpener = undone.items.find((row) => row.id === item.id) || undone.items[0];
                    if (restoredOpener) {
                        const restoredDraft = buildDraftFromItem(restoredOpener);
                        setDraft((currentDraft) => ({
                            ...restoredDraft,
                            ...getPendingDraftEdits(currentDraft, updatedDraft),
                        }));
                    }
                    undone.items.forEach((restored) => onUpdate(restored));
                    showToast('Action undone.', 'success');
                } catch (err: any) {
                    showToast(err?.response?.data?.detail || 'Could not undo the last action.', 'error');
                }
            };
            showToast(messages[attention], 'success', undo);
            return updated;
        } catch (e: any) {
            showToast(e?.response?.data?.detail || 'Failed to apply source rule.', 'error');
            throw e;
        } finally {
            quickUpdateInFlightRef.current = false;
            setSaving(false);
        }
    };

    /** C29-1: "Looks safe" / "It's a scam", one tap each; the toast is the confirmation. */
    const decideSafety = async (verdict: SafetyVerdict) => {
        if (!item || saving || quickUpdateInFlightRef.current) return;
        if (!item.safety?.revision) return;
        const itemId = item.id;
        quickUpdateInFlightRef.current = true;
        setSaving(true);
        try {
            const updated = await workItemsApi.putSafetyVerdict(itemId, verdict, item.safety.revision);
            const priorDraft = buildDraftFromItem(item);
            const updatedDraft = buildDraftFromItem(updated);
            setDraft((currentDraft) => ({
                ...updatedDraft,
                ...getPendingDraftEdits(currentDraft, priorDraft),
            }));
            onUpdate(updated);
            const undo = async () => {
                try {
                    const restored = await workItemsApi.undoSafetyVerdict(itemId);
                    setDraft((currentDraft) => ({
                        ...buildDraftFromItem(restored),
                        ...getPendingDraftEdits(currentDraft, updatedDraft),
                    }));
                    onUpdate(restored);
                    showToast('Action undone.', 'success');
                } catch (err: any) {
                    showToast(err?.response?.data?.detail === 'safety_verdict_verdict_not_current'
                        ? 'This item changed, so this can no longer be undone.'
                        : 'Could not undo the last action.', 'error');
                }
            };
            showToast(verdict === 'safe' ? 'Marked safe.' : 'Marked as a scam and archived.', 'success', undo);
        } catch (e: any) {
            if (e?.response?.status === 409) {
                try {
                    onUpdate(await workItemsApi.getWorkItem(itemId));
                } catch { /* Keep the current warning if the refresh also fails. */ }
            }
            showToast('Could not save that. Try again.', 'error');
        } finally {
            quickUpdateInFlightRef.current = false;
            setSaving(false);
        }
    };

    const applyAttachMerge = async () => {
        if (!item?.merge_suggestion?.target_id || saving || quickUpdateInFlightRef.current) return;
        const targetTitle = item.merge_suggestion.title || 'related item';
        quickUpdateInFlightRef.current = true;
        setSaving(true);
        try {
            const response = await workItemsApi.attachMerge(item.id, item.merge_suggestion.target_id);
            onUpdate({
                ...item,
                state: 'ARCHIVED',
                needs_action: false,
                merge_suggestion: null,
            });
            onUpdate(response.item);
            showToast(`Attached to ${response.item.title || targetTitle}`, 'success');
            return response.item;
        } catch (e: any) {
            showToast(e?.response?.data?.detail || 'Failed to attach item.', 'error');
            throw e;
        } finally {
            quickUpdateInFlightRef.current = false;
            setSaving(false);
        }
    };

    const applyUnmergeLast = async () => {
        if (!item || saving || quickUpdateInFlightRef.current) return;
        quickUpdateInFlightRef.current = true;
        setSaving(true);
        try {
            const response = await workItemsApi.unmergeLast(item.id);
            setDraft((currentDraft) => ({
                ...buildDraftFromItem(response.item),
                ...getPendingDraftEdits(currentDraft, buildDraftFromItem(item)),
            }));
            onUpdate(response.item);
            if (response.restored_item) {
                onUpdate(response.restored_item);
            }
            showToast('Separated last update', 'success');
            return response.item;
        } catch (e: any) {
            showToast(e?.response?.data?.detail || 'Failed to undo last merge.', 'error');
            throw e;
        } finally {
            quickUpdateInFlightRef.current = false;
            setSaving(false);
        }
    };

    const revealTeachStrip = () => {
        const el = teachStripRef.current;
        if (!el) return;
        if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
        el.focus();
    };
    const confirmCurrentDomain = async () => {
        if (!item) return;
        await quickUpdate(
            {
                domain: item.domain || 'general',
                confirm_domain_placement: true,
            },
            { successMessage: 'Domain placement confirmed.' },
        );
    };
    const moveToGeneral = async () => {
        if (!item) return;
        await quickUpdate(
            { domain: 'general' },
            { successMessage: 'Moved to General.', undoable: true },
        );
    };
    const runMenuAction = async (action: () => Promise<unknown> | unknown) => {
        closeActionsMenu();
        await action();
    };

    const performRerunEnrichment = async () => {
        if (!item) return;
        setEnrichRerunBusy(true);
        try {
            const res = await workItemsApi.enrichWorkItem(item.id, { force_overwrite: true });
            setDraft(buildDraftFromItem(res.work_item));
            onUpdate(res.work_item);
            showToast('Enrichment updated', 'success');
        } catch (e: any) {
            const status = e?.response?.status as number | undefined;
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code === 'AI_CREDITS_EXHAUSTED') {
                const m =
                    typeof (detail as { message?: string }).message === 'string'
                        ? (detail as { message: string }).message
                        : 'AI credits are exhausted for this cycle. You can still edit and save this item.';
                showToast(m, 'info');
                return;
            }
            if (status === 503) {
                const d = e?.response?.data?.detail;
                const msg =
                    typeof d === 'string' && d.trim()
                        ? d
                        : 'Enrichment is temporarily unavailable. Try again later.';
                showToast(msg, 'error');
                return;
            }
            let msg = 'Enrichment failed.';
            if (typeof detail === 'string' && detail.trim()) {
                msg = detail;
            } else if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
                msg = detail.message;
            } else if (Array.isArray(detail)) {
                msg = detail
                    .map((row: { msg?: string }) =>
                        typeof row?.msg === 'string' ? row.msg : JSON.stringify(row),
                    )
                    .join(' ');
            }
            showToast(msg || 'Enrichment failed.', 'error');
        } finally {
            setEnrichRerunBusy(false);
        }
    };

    const rerunEnrichment = async () => {
        if (!item) return;
        if (isDirty) {
            setConfirmRerunEnrichment(true);
            return;
        }
        await performRerunEnrichment();
    };

    const confirmRerunDiscardLocalAndRun = async () => {
        setConfirmRerunEnrichment(false);
        await performRerunEnrichment();
    };

    const markDone = async () => {
        await quickUpdate(
            { state: 'DONE', needs_action: false },
            { successMessage: 'Marked complete.', undoable: true, advance: true },
        );
    };
    // Shared by the header button and the ⋮ menu — one mutation, one Undo.
    const reopenItem = () => quickUpdate(
        { state: 'ACTIVE', needs_action: true },
        { successMessage: 'Item reopened.', undoable: true },
    );
    // Shared by the header button, the Triage action row, and the ⋮ menu — one mutation, one Undo.
    const startItem = () => quickUpdate(
        { state: 'ACTIVE', needs_action: true },
        { successMessage: 'Item started.', undoable: true, advance: true },
    );
    const classifyApplication = () => quickUpdate(
        { type: 'APPLICATION' },
        { successMessage: 'Classified as Application.', undoable: true },
    );
    const classifyTask = () => quickUpdate(
        { type: 'TASK' },
        { successMessage: 'Classified as Task.', undoable: true },
    );
    const dismissTriage = async () => {
        await quickUpdate(
            {
                state: 'DONE',
                needs_action: false,
                meta: {
                    disposition: 'dismissed',
                    dismissed_at: new Date().toISOString(),
                },
            },
            { successMessage: 'Item dismissed.', undoable: true, advance: true },
        );
    };
    const toggleArchive = async () => {
        const shouldUnarchive = isArchivedState;
        const newState = shouldUnarchive ? 'ACTIVE' : 'ARCHIVED';
        // Archive → clear needs_action; Unarchive → preserve existing needs_action (don't force true)
        const updates: WorkItemUpdate = { state: newState };
        if (!shouldUnarchive) updates.needs_action = false;
        // Preserve clean-item auto-advance; otherwise keep the item and Undo available.
        await quickUpdate(updates, {
            successMessage: shouldUnarchive ? 'Item unarchived.' : 'Item archived.',
            undoable: true,
            advance: !shouldUnarchive,
        });
    };
    // Snooze sets snoozed_until and clears needs_action. Does NOT change due_date.
    const snooze = (days: number) => quickUpdate(
        { snoozed_until: addDays(new Date(), days), needs_action: false },
        { successMessage: `Snoozed for ${days} day${days === 1 ? '' : 's'}.`, undoable: true, advance: true },
    );

    const performDelete = async () => {
        if (!item) return;
        setSaving(true);
        try {
            await workItemsApi.deleteWorkItem(item.id);
            invalidateInFlightFamilyNav();
            // The toast outlives the drawer (see the `!open` branch), so the person sees
            // what happened after it closes. Delete is permanent: no Undo.
            showToast('Item deleted.', 'success');
            onDelete(item.id);
            onClose();
        } catch (e: any) {
            console.error('Failed to delete:', e);
            showToast('Failed to delete: ' + (e.response?.data?.detail || e.message), 'error');
        } finally {
            setSaving(false);
            setConfirmDelete(false);
        }
    };

    const requestRoute = (route: string) => {
        if (saving || quickUpdateInFlightRef.current) {
            showToast('An update is still in progress.', 'info', toastUndo || undefined);
            return;
        }
        invalidateInFlightFamilyNav();
        // dirtyRef, not the isDirty closed over at click time: the family lookup may still be in flight.
        if (dirtyRef.current) {
            pendingNavigationIdRef.current = null;
            pendingRouteRef.current = () => navigate(route);
            setConfirmDiscard(true);
            return;
        }
        navigate(route);
    };
    // Already on Workbench, Open in Workbench only earns its place when it opens a
    // narrower pile (a document family); otherwise it reopens the view you are in.
    const onWorkbenchHost = typeof window !== 'undefined' && window.location.pathname.startsWith('/app/workbench');
    const showOpenInWorkbench = !onWorkbenchHost
        || Boolean(item && familyResolved && documentFamilyWorkbenchAnchor(item.id, family));
    const openInWorkbench = () => {
        if (!item) return;
        const currentId = item.id;
        const leave = (nextFamily: SourceFamily | null) => {
            if (openItemIdRef.current !== currentId) return;
            requestRoute(workbenchPathForDocumentFamily(currentId, nextFamily));
        };
        // The label is already on screen once the family has answered. Use that payload.
        if (familyResolved && family?.discoverable) {
            leave(family);
            return;
        }
        if (!mayHaveSourceFamily(item)) {
            leave(null);
            return;
        }
        void workItemsApi.getSourceFamily(currentId).then(leave).catch(() => leave(null));
    };

    const openSourceDocument = async () => {
        if (!item?.document_id) return;
        try {
            const result = await documentsApi.getDownloadUrl(item.document_id);
            window.open(result.url, '_blank', 'noopener');
            setSourceDocumentState('available');
            setSourceDocumentMessage('');
        } catch (e: any) {
            const status = e?.response?.status;
            const code = e?.response?.data?.code;
            if (status === 410 || code === 'DOCUMENT_DELETED') {
                setSourceDocumentState('deleted');
                setSourceDocumentMessage('This file was removed and can no longer be opened.');
                showToast('Source document was deleted.', 'error');
                return;
            }
            if (status === 404 || code === 'DOCUMENT_MISSING') {
                setSourceDocumentState('missing');
                setSourceDocumentMessage("This file was removed or can't be found.");
                showToast("Source document is unavailable (missing file).", 'error');
                return;
            }
            console.error('Failed to fetch document URL:', e);
            setSourceDocumentState('unknown_error');
            setSourceDocumentMessage('Unable to verify source document right now.');
            showToast('Failed to open source document.', 'error');
        }
    };

    const unlinkSourceDocument = async () => {
        await quickUpdate({ document_id: null }, { successMessage: 'Source document link removed.', undoable: true });
    };

    const openUploadReplacement = () => {
        requestRoute('/app/documents');
    };

    const hasLinkedEmail = Boolean(linkedEmailMessageId(item));
    const handleEmailSafetyChange = (safety: WorkItemSafety | null) => {
        if (item && JSON.stringify(item.safety ?? null) !== JSON.stringify(safety)) {
            onUpdate({ ...item, safety });
        }
    };
    // C29-1 T2: an email item at high takes the same text-only stop as a document at
    // high, held or not, until a current "Looks safe".
    const emailRisk = useMemo(() => emailScamRisk(item?.safety), [item?.safety]);
    const nextStepRisk = scamRisk ?? emailRisk;
    const nextStep = useMemo(
        () => {
            if (item && (isSourceHomeItem(item) || virtualSource)) return null;
            return deriveNextStep({
                item,
                scamRisk: nextStepRisk,
                decisionGuide,
                invoiceData,
                hasLinkedEmail,
                documentAvailable: sourceDocumentState === 'available',
            });
        },
        [item, virtualSource, nextStepRisk, decisionGuide, invoiceData, hasLinkedEmail, sourceDocumentState],
    );
    const drawerStakes = useMemo(
        () => deriveDrawerStakes({ item, invoiceData }),
        [item, invoiceData],
    );
    // Overview first unless it would show nothing; then Edit, where the user can act.
    // Read the stored item only. Live invoiceData / extractionFields / nextStep still
    // belong to the previous item on the first render after a pile jump, and the
    // opening-tab effect must not wait for the intel reset.
    const storedMeta = (item?.meta || {}) as Record<string, any>;
    const storedInvoice = storedMeta.invoice_data || null;
    const storedFields = storedMeta.fields || null;
    openingFacetRef.current = item?.id && !buildOverviewModel({
        item,
        draft: { summary: item.summary, notes: item.notes, meta: item.meta },
        nextStep: item && !(isSourceHomeItem(item) || virtualSource)
            ? deriveNextStep({
                item,
                scamRisk: null,
                decisionGuide: null,
                invoiceData: storedInvoice,
                hasLinkedEmail,
                documentAvailable: Boolean(item.document_id),
            })
            : null,
        stakes: deriveDrawerStakes({ item, invoiceData: storedInvoice }),
        invoiceData: storedInvoice,
        extractionFields: storedFields,
        hideReadSummary: showSourceTile,
    }).isEmpty ? 'triage' : 'work';

    const handleNextStepCta = async (kind: NextStepCtaKind, payload?: string) => {
        if (nextStepRisk?.level === 'high') return;
        if (kind === 'copy_template' && payload) {
            await handleCopy(payload);
            return;
        }
        if (kind === 'open_document') {
            await openSourceDocument();
            return;
        }
        if (kind === 'open_url' && payload) {
            window.open(payload, '_blank', 'noopener,noreferrer');
            return;
        }
        if (kind === 'open_email' && item) {
            selectFacet('triage');
            setEmailViewerExpanded(true);
            try {
                const res = await client.get(`/work-items/${item.id}/email/navigation`);
                if (emailScamRisk(res.data?.safety)?.level === 'high') {
                    handleEmailSafetyChange(res.data.safety);
                    return;
                }
                if (!res.data?.gmail_thread_id || (!res.data?.mailbox_email_address && res.data?.gmail_web_authuser_index == null)) {
                    throw new Error('Mailbox navigation unavailable');
                }
                const url = buildGmailThreadDeepLink({
                    threadId: res.data?.gmail_thread_id,
                    mailboxEmail: res.data?.mailbox_email_address ?? null,
                    gmailWebAuthuserIndex: res.data?.gmail_web_authuser_index ?? null,
                });
                window.open(url, '_blank', 'noopener,noreferrer');
            } catch {
                showToast('Could not open this email in its mailbox. Try opening the original message.', 'error');
            }
        }
    };

    const triageActions: TriageActions | undefined = item && isTriageState && !virtualSource ? {
                            onStart: () => void startItem(),
                            onSnooze: (days) => void snooze(days),
                            onDismiss: () => void dismissTriage(),
                            onClassifyApplication: () => void classifyApplication(),
                            onClassifyTask: () => void classifyTask(),
                            onIgnoreSource: isSourceTeachEligible(item)
                                ? () => void applySourceRule('ignore')
                                : undefined,
                            currentType: draft.type ?? item.type ?? null,
                            disabled: saving,
    } : undefined;

    const similarItemNotificationActive =
        similarItemSnackbarOpen || similarItemPollRef.current !== null;

    const similarItemNotificationUi = (
        <>
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="WORKITEM_DRAWER_SIMILAR_ITEM_STATUS"
                sx={visuallyHiddenSx}
            >
                {similarItemStatusMessage}
            </Box>

            <Snackbar
                open={similarItemSnackbarOpen}
                autoHideDuration={10000}
                onClose={() => setSimilarItemSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSimilarItemSnackbarOpen(false)}
                    severity="info"
                    sx={{ width: '100%' }}
                    data-testid="WORKITEM_DRAWER_SIMILAR_ITEM_SNACKBAR"
                    action={
                        similarItemReadyBatchId ? (
                            <Button
                                color="inherit"
                                size="small"
                                component={RouterLink}
                                to={`/app/similar-item-suggestions?batch_id=${encodeURIComponent(similarItemReadyBatchId)}`}
                                onClick={() => setSimilarItemSnackbarOpen(false)}
                            >
                                Review
                            </Button>
                        ) : undefined
                    }
                >
                    Similar items are ready for review.
                </Alert>
            </Snackbar>
        </>
    );

    const toastUi = (
        <Snackbar
            open={toastOpen}
            autoHideDuration={toastUndo ? null : 4000}
            onClose={(_, reason) => { hideToast(reason); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert
                severity={toastSeverity}
                sx={{ width: '100%' }}
                action={
                    <>
                        {toastUndo ? (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleToastUndo}
                                disabled={saving}
                                data-testid="WB_DRAWER_TOAST_UNDO"
                            >
                                Undo
                            </Button>
                        ) : null}
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => hideToast()}
                            aria-label="Dismiss notification"
                            data-testid="WB_DRAWER_TOAST_DISMISS"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    </>
                }
            >
                {toastMessage}
            </Alert>
        </Snackbar>
    );

    const openEvidence = hasIntel
        ? () => {
            if (sourceHomeAccordion) setSourceHomePanel('intel');
            else selectFacet('intel');
        }
        : undefined;

    const renderFacet = (facet: Facet) => {
        if (facet === 'triage') {
            return (
                <DrawerFacetTriage
                    item={item}
                    draft={draft}
                    onFieldChange={handleFieldChange}
                    saving={saving}
                    showToast={showToast}
                    onEmailExpandedChange={setEmailViewerExpanded}
                    emailViewerExpanded={emailViewerExpanded}
                    onMailboxGmailAuthIssue={onMailboxGmailAuthIssue}
                    onEmailSafetyChange={handleEmailSafetyChange}
                    scamRisk={nextStepRisk}
                    invoiceData={invoiceData}
                    extractionFields={extractionFields}
                    nextStep={nextStep}
                    stakes={drawerStakes}
                    onNextStepCta={handleNextStepCta}
                    hideReadSummary={showSourceTile}
                />
            );
        }
        if (facet === 'work') {
            return (
                <DrawerFacetWork
                    item={item}
                    draft={draft}
                    onFieldChange={handleFieldChange}
                    saving={saving}
                    showToast={showToast}
                    onEmailExpandedChange={setEmailViewerExpanded}
                    emailViewerExpanded={emailViewerExpanded}
                    onMailboxGmailAuthIssue={onMailboxGmailAuthIssue}
                    isTriageState={isTriageState}
                    isJobDomain={isJobDomain}
                    timeZone={effectiveUserTimeZone(bootstrap?.user?.timezone)}
                    domainMenu={domainMenu}
                    domainFieldEntries={domainFieldEntries}
                    currentStateLabel={currentStateLabel}
                    showEventDate={showEventDate}
                    setShowEventDate={setShowEventDate}
                    suggestedActionDate={suggestedActionDate}
                    prepDaysUsed={prepDaysUsed}
                    detectedEventType={detectedEventType}
                    applySuggestion={applySuggestion}
                    startOnEventDay={startOnEventDay}
                    nextStep={nextStep}
                    stakes={drawerStakes}
                    onNextStepCta={handleNextStepCta}
                />
            );
        }
        if (facet === 'intel') {
            return (
                <DrawerFacetIntel
                    item={item}
                    draft={draft}
                    onFieldChange={handleFieldChange}
                    saving={saving}
                    showToast={showToast}
                    onRetryDecisionGuide={retryDecisionGuideFetch}
                    decisionGuideFetchStatus={decisionGuideFetchStatus}
                    decisionGuide={decisionGuide}
                    scamRisk={scamRisk}
                    invoiceData={invoiceData}
                    contactInfo={contactInfo}
                    noticeData={noticeData}
                    extractionFields={extractionFields}
                    reviewRequired={reviewRequired}
                    reviewReasons={reviewReasons}
                    scamExpanded={scamExpanded}
                    onToggleScamDetails={toggleScamDetails}
                    onCopy={handleCopy}
                />
            );
        }
        return null;
    };

    if (!open) {
        // Keep feedback surfaces alive after the drawer closes so action toasts
        // (e.g. Archive / Dismiss confirmations) are not cut off mid-display.
        if (!similarItemNotificationActive && !toastOpen) {
            return null;
        }
        return (
            <>
                {toastUi}
                {similarItemNotificationUi}
            </>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            slotProps={{
                transition: {
                    onEntered: () => {
                        drawerPanelRef.current?.focus();
                    },
                },
            }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 560 },
                    height: '100%',
                    maxHeight: '100%',
                    overflow: 'hidden',
                    bgcolor: 'var(--color-bg-secondary)',
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid var(--color-border)',
                    // One voice for every button in the drawer: sentence case.
                    '& .MuiButton-root': { textTransform: 'none' },
                }
            }}
        >
            <Box
                ref={drawerPanelRef}
                id="wb-drawer-panel"
                data-testid="WB_DRAWER_PANEL"
                tabIndex={-1}
                aria-labelledby="wb-drawer-title"
                onKeyDown={handlePanelKeyDown}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    outline: 'none',
                    overflow: 'hidden',
                }}
            >
            {/* 1. Header & Quick Actions Layer */}
            <Box
                sx={{
                    flexShrink: 0,
                    pt: isSmallScreen ? 0.75 : compactHeaderActive ? 1 : 1.5,
                    pr: isSmallScreen ? 0.5 : 1.5,
                    pb: compactHeaderActive ? 0.75 : isSmallScreen ? 1.25 : 1.5,
                    pl: isSmallScreen ? 2 : 2.5,
                    borderBottom: '1px solid var(--color-border)',
                    bgcolor: 'var(--color-bg-secondary)',
                    zIndex: 10,
                    transition: `padding ${motionTransition}`,
                }}
            >
                <WorkItemDrawerHeader
                    item={item}
                    compactHeaderActive={compactHeaderActive}
                    motionTransition={motionTransition}
                    sourceChip={sourceChip}
                    lifeAreaChip={lifeAreaChip}
                    dueStatusLine={headerDueStatus}
                    sourceDocumentState={sourceDocumentState}
                    sourceDocumentMessage={sourceDocumentMessage}
                    detailsControl={item && !virtualSource ? (
                        <Button data-testid="WB_TRUST_DETAILS_TOGGLE" aria-expanded={trustDetailsExpanded}
                            aria-controls={trustDetailsId} onClick={toggleTrustDetails}
                            sx={{ minHeight: 44, textTransform: 'none', color: 'var(--color-text-secondary)' }}>
                            Source details
                        </Button>
                    ) : undefined}
                    showActionsMenu={showActionsMenu}
                    onOpenActionsMenu={openActionsMenu}
                    onClose={handleClose}
                    onOpenUploadReplacement={openUploadReplacement}
                    onUnlinkSourceDocument={unlinkSourceDocument}
                    getTitle={getTitle}
                    derivedTasks={derivedTasksText}
                    titleOverride={titleOverride}
                    suppressChildActions={virtualSource}
                    primaryActionLabel={headerPrimaryLabel}
                    primaryActionIcon={primaryActionIcon}
                    primaryActionColor={primaryActionColor}
                    onPrimaryAction={onPrimaryAction}
                    primaryActionDisabled={saving}
                    navigation={showNavigation && navigation ? {
                        index: navigation.index,
                        total: navigation.total,
                        pileLabel: navigation.pileLabel,
                        hasPrev: Boolean(navigation.prevId),
                        hasNext: Boolean(navigation.nextId),
                        onPrev: () => navigateTo(navigation.prevId),
                        onNext: () => navigateTo(navigation.nextId),
                        disabled: saving,
                    } : undefined}
                />
            </Box>

            {/* 1. Actions Menu Overlay */}
            {item && (
                <WorkItemDrawerActionsMenu
                    open={actionsMenuOpen}
                    anchorEl={actionsMenuAnchorEl}
                    onClose={closeActionsMenu}
                    saving={saving}
                    isDoneState={isDoneState}
                    isTriageState={isTriageState}
                    hasActiveSnooze={hasActiveSnooze}
                    isArchivedState={isArchivedState}
                    hasDocument={Boolean(item.document_id)}
                    sourceDocumentState={sourceDocumentState}
                    onRunAction={runMenuAction}
                    onReopen={reopenItem}
                    onMarkDone={markDone}
                    onStart={startItem}
                    onDismiss={dismissTriage}
                    onUnsnooze={() => quickUpdate({ snoozed_until: null, needs_action: true }, { successMessage: 'Snooze removed.', undoable: true })}
                    onSnooze={snooze}
                    onClassifyApplication={classifyApplication}
                    onClassifyTask={classifyTask}
                    onOpenInWorkbench={showOpenInWorkbench ? openInWorkbench : undefined}
                    onOpenSourceDocument={openSourceDocument}
                    onToggleArchive={toggleArchive}
                    onRequestDelete={() => setConfirmDelete(true)}
                    onRerunEnrichment={rerunEnrichment}
                    enrichRerunBusy={enrichRerunBusy}
                    aiCreditsBootstrapBlockingLoad={bootstrapLoading && !bootstrap}
                    aiCreditsBootstrapError={bootstrapError}
                    aiCreditsEntitlements={bootstrap?.entitlements ?? bootstrap?.user?.entitlements}
                    showSourceRuleActions={isSourceTeachEligible(item)}
                    currentSourceRuleAttention={currentSourceRuleAttention(item)}
                    onKeepAsTask={() => applySourceRule('action')}
                    onWatchSource={() => applySourceRule('watch')}
                    onIgnoreSource={() => applySourceRule('ignore')}
                    autoAdvanceEnabled={navigation ? autoAdvancePref !== 'off' : undefined}
                    onToggleAutoAdvance={navigation ? toggleAutoAdvance : undefined}
                    blockComplete={blockComplete}
                    hideTriageVerbs={triageVerbsInBarOnly}
                    allowReopen={!virtualSource}
                    sourceDocumentOnly={virtualSource}
                />
            )}

            {/* One scrollport for the detail body. A nested Overview scroller left
                the family strip (the middle of a phone pane) unable to move content
                below the fold. The header stays outside; Save stays pinned below. */}
            <Box
                ref={contentScrollRef}
                id="wb-drawer-body"
                data-testid="WB_DRAWER_BODY"
                onScroll={handleBodyScroll}
                sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                }}
            >
            {provenanceFact && <DrawerProvenanceLine fact={provenanceFact} />}
            {showSourceTile && sourceSummary && (
                <DrawerSourceTile summary={sourceSummary} />
            )}
            <DrawerRiskAlert
                scamRisk={scamRisk}
                onOpenEvidence={virtualSource ? undefined : openEvidence}
                emailSafety={virtualSource ? null : item?.safety}
                onDecide={(verdict) => { void decideSafety(verdict); }}
                deciding={saving}
            />
            {shownFamily?.discoverable && !showSourceHome && (
                <SourceFamilyStrip
                    tasks={familyTasks}
                    activeTaskId={item?.id ?? null}
                    onOpenSource={() => { void openSourceHome(); }}
                    onOpenTask={(taskId) => { void openFamilyTask(taskId); }}
                    homeEyebrow={shownFamily.source_kind === 'email'
                        ? 'From this email'
                        : shownFamily.source_kind === 'document'
                            ? 'From this document'
                            : null}
                    homeTitle={shownFamily.source_title || null}
                />
            )}
            {showSourceHome && shownFamily?.discoverable && (
                <SourceFamilyProgress tasks={familyTasks} />
            )}

            {/* 2. Facet Tab Strip — hidden on the Shape A source presentation
                (accordion under the verbs) and on the virtual source overlay. */}
            {!virtualSource && (
            <>
            {showFacetTabs && (
            <Box
                role="tablist"
                aria-label="Drawer facets"
                onKeyDown={handleTabListKeyDown}
                sx={{
                    display: 'flex',
                    gap: 0,
                    borderBottom: '1px solid var(--color-border)',
                    bgcolor: 'var(--color-bg-secondary)',
                    px: 3,
                }}
            >
                {visibleFacetTabs.map((facetTab) => {
                    const { facet, label, testId } = facetTab;
                    const isActive = currentFacet === facet;
                    const hasIntelIndicator = facet === 'intel' && (
                        decisionGuide
                        || decisionGuideFetchStatus === 'loading'
                        || (scamRisk && scamRisk.level !== 'low')
                    );
                    return (
                        <Button
                            key={facet}
                            id={`wb-drawer-tab-${facet}`}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls="wb-drawer-tabpanel"
                            tabIndex={isActive ? 0 : -1}
                            data-testid={testId}
                            onClick={() => selectFacet(facet)}
                            sx={{
                                flex: 1,
                                borderRadius: 0,
                                textTransform: 'none',
                                fontWeight: isActive ? 700 : 500,
                                fontSize: '0.8rem',
                                color: isActive ? 'var(--color-accent-text)' : 'var(--color-text-secondary)',
                                borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                                transition: `color ${motionTransition}, border-color ${motionTransition}`,
                                py: 1,
                                minWidth: 0,
                                '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.06)' },
                                '&:focus-visible': {
                                    outline: '2px solid var(--color-accent)',
                                    outlineOffset: '2px',
                                },
                                position: 'relative',
                            }}
                        >
                            {label}
                            {hasIntelIndicator && (
                                <Box
                                    component="span"
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: 'var(--color-accent)',
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                    }}
                                />
                            )}
                        </Button>
                    );
                })}
            </Box>
            )}

            {/* 3. Scrollable Facet Content. On the source home this is only Source details;
                Overview and Edit live in the accordion under the verbs. */}
            <Box
                role={showFacetTabs ? 'tabpanel' : undefined}
                id={showFacetTabs ? 'wb-drawer-tabpanel' : undefined}
                aria-labelledby={showFacetTabs ? `wb-drawer-tab-${currentFacet}` : undefined}
                sx={sourceHomeAccordion
                    ? undefined
                    : { p: { xs: 2, sm: 3 } }}
            >
            {item && (
                <WorkItemTrustStrip
                    item={item}
                    expanded={trustDetailsExpanded}
                    detailsId={trustDetailsId}
                    sourceDocumentState={sourceDocumentState}
                    domainActionsDisabled={saving}
                    lifeAreaOptions={domainMenu.filter((entry) => !entry.disabled).map((entry) => ({
                        key: entry.key,
                        label: entry.label,
                    }))}
                    onConfirmDomain={confirmCurrentDomain}
                    onMoveToGeneral={moveToGeneral}
                    onCorrectLifeArea={(domainKey) => {
                        void quickUpdate(
                            { domain: domainKey },
                            { successMessage: 'Life area updated.', undoable: true },
                        );
                    }}
                >
                    <WorkItemLineageSection
                        item={item}
                        sourceDocumentState={sourceDocumentState}
                        onOpenSourceDocument={openSourceDocument}
                        motionTransition={motionTransition}
                        onRevealSourceRule={revealTeachStrip}
                        onUnmergeLast={applyUnmergeLast}
                        unmergeSaving={saving}
                    />
                    <WorkItemClassificationPanel item={item} suppressSuspicionCue={trustModel?.isCritical} />
                    {triageActions && <DrawerTriageActionRow actions={triageActions} mode="classification" />}
            {item && isSourceTeachEligible(item) && (
                <WorkItemSourceTeachStrip
                    ref={teachStripRef}
                    item={item}
                    saving={saving}
                    onKeepAsTask={() => applySourceRule('action')}
                    onWatchSource={() => applySourceRule('watch')}
                    onIgnoreSource={() => applySourceRule('ignore')}
                />
            )}

            {item && isMergeAttachEligible(item) && (
                <WorkItemMergeAttachStrip
                    item={item}
                    saving={saving}
                    onAttach={applyAttachMerge}
                />
            )}
                </WorkItemTrustStrip>
            )}

                {!sourceHomeAccordion && renderFacet(item ? currentFacet : 'work')}
            </Box>
            </>
            )}

            {triageActions && (
                <DrawerTriageActionRow actions={triageActions} mode="actions" layout="bar" />
            )}
            {showSourceHome && shownFamily?.discoverable && (
                <SourceHomeSteps
                    tasks={familyTasks}
                    onOpenTask={(taskId) => { void openFamilyTask(taskId); }}
                />
            )}
            {sourceHomeAccordion && (
                <DrawerSourceHomeAccordion
                    openPanel={sourceHomePanel}
                    showEvidence={hasIntel}
                    onToggle={toggleSourceHomePanel}
                    overview={sourceHomePanel === 'triage' ? renderFacet('triage') : null}
                    edit={sourceHomePanel === 'work' ? renderFacet('work') : null}
                    evidence={sourceHomePanel === 'intel' ? renderFacet('intel') : null}
                />
            )}
            </Box>
            {/* Phone: Mark complete and Reopen sit in a bar pinned under the body (D2 phone);
                Triage keeps Start · Snooze · Dismiss in its sticky bar inside the body. */}
            {isSmallScreen && headerPrimaryLabel && onPrimaryAction && (
                <Box
                    data-testid="WB_DRAWER_PRIMARY_BAR"
                    sx={{ flexShrink: 0, px: 2, py: 1.5, borderTop: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-secondary)' }}
                >
                    <Button
                        variant="contained"
                        color={primaryActionColor}
                        onClick={onPrimaryAction}
                        disabled={saving}
                        fullWidth
                        startIcon={primaryActionIcon}
                        data-testid="WB_DRAWER_PRIMARY_ACTION"
                        sx={{
                            minHeight: 48,
                            '&:focus-visible': {
                                outline: '2px solid var(--color-accent)',
                                outlineOffset: '2px',
                            },
                        }}
                    >
                        {headerPrimaryLabel}
                    </Button>
                </Box>
            )}
            {/* 3. Sticky Footer for Form Actions — only rendered when there is something to save.
                Delete lives in the actions menu (DANGER section) so a destructive action is not
                permanently one pixel from Save. */}
            {showSaveButton && !virtualSource && (
                <Box sx={{ flexShrink: 0, p: 2, borderTop: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-secondary)', display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        onClick={() => void save()}
                        disabled={saving}
                        fullWidth
                        size="large"
                        sx={{
                            bgcolor: 'var(--color-accent)',
                            color: 'var(--color-on-accent)',
                            '&:hover': { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' },
                            '&:focus-visible': {
                                outline: '2px solid var(--color-accent)',
                                outlineOffset: '2px',
                            },
                        }}
                    >
                        {item ? 'Save changes' : 'Create item'}
                    </Button>
                </Box>
            )}

            </Box>

            <ConfirmationDialog
                open={confirmDelete}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={performDelete}
                title="Delete work item"
                message="Are you sure you want to permanently delete this item? This action cannot be undone."
                confirmLabel="Delete"
                isDestructive
            />

            <ConfirmationDialog
                open={confirmDiscard}
                onCancel={cancelDiscard}
                onConfirm={discardAndContinue}
                onSave={() => void save(forceClose)}
                cancelTestId={WB_DRAWER_DIRTY_TEST_IDS.cancel.testId}
                confirmTestId={WB_DRAWER_DIRTY_TEST_IDS.discard.testId}
                saveTestId={WB_DRAWER_DIRTY_TEST_IDS.save.testId}
                isLoading={saving}
                title="Unsaved changes"
                message={pendingRouteRef.current
                    ? 'Save your edits before leaving, or discard them?'
                    : pendingNavigationIdRef.current
                    ? 'You have unsaved edits. Discard them and move to the next item?'
                    : 'You have unsaved edits. Discard them and close?'}
                confirmLabel="Discard"
                isDestructive
            />

            <ConfirmationDialog
                open={confirmRerunEnrichment}
                onCancel={() => setConfirmRerunEnrichment(false)}
                onConfirm={() => void confirmRerunDiscardLocalAndRun()}
                title="Unsaved changes"
                message="You have unsaved edits in the drawer. Save them first, or continue to discard local changes and replace fields with a fresh enrichment from the server."
                confirmLabel="Continue"
                isDestructive
                isLoading={enrichRerunBusy}
            />

            {/* Toast Notification */}
            {toastUi}

            {similarItemNotificationUi}
        </Drawer >
    );
};

export default WorkItemDrawer;
